/**
 * 3sixteen API helpers — currently just the Back-In-Stock waitlist
 * adapter. Wraps the BIS third-party global with our error-handling
 * contract and structured logging.
 *
 * Cross-module compat shim: this module sets `window.api = { addToWaitlist }`
 * so the waitlist can be driven from the console when debugging a signup.
 * Drop the shim in Phase 13.
 *
 * Migrated from theme/js/plugins/3sixteen-api.js (IIFE → ESM, Phase 11).
 *
 * Failures are reported to `settings.error` as `{ errors, waitlistError }`.
 * `waitlistError` is `'no_email'` when the caller gave us no address to
 * register, and absent for every other failure; `errors` carries BIS's own
 * payload when the server answered. User-facing copy is the caller's job —
 * see add-item-form.esm.js.
 *
 * Two BIS behaviors this has to work around (theme/js/_shopify/bis.js is a
 * reference copy of the app's script — the app injects its own):
 *
 *   - `_buildCreatePayload` only sets `notification.channel` when the email
 *     argument is truthy, and `create`'s guard for the resulting payload
 *     compares against the *string* `'undefined'`, so it doesn't catch a
 *     channel-less payload — a blank email ships a signup with neither an
 *     email nor a channel and the server rejects it. Hence the email check
 *     below, and passing `channel` explicitly instead of relying on that
 *     defaulting.
 *   - The return value is not necessarily a native Promise. `BIS.Promise`
 *     returns `this` from `then()` and has no `catch()` at all, so `.catch`
 *     is attached only when it exists, and one request must settle exactly
 *     once across both paths.
 *
 * Source path: theme/js/plugins/3sixteen-api.esm.js
 * Built name : ./3sixteen-api.js (sibling imports must use the built name)
 *
 * AI: Mostly written by AI (originally 2026-03-29; ESM rewrite 2026-05-05)
 * Date: 2026-05-05
 * Model: Claude Opus 4.7
 * Updated: 2026-09-08 - Validate the email, send an explicit channel, report
 *          machine-readable failure reasons, and settle each request once.
 */

import { log } from './log.js';

export function addToWaitlist(settings, callback) {
  if (typeof settings !== 'object' || settings === null) {
    log('[Waitlist API] Invalid settings, expected object');
    return false;
  }

  let settled = false;
  const settle = (kind, payload) => {
    if (settled) return;
    settled = true;
    if (typeof settings[kind] === 'function') settings[kind](payload);
  };
  // `no_email` is the only reason a caller distinguishes; every other failure
  // is just a failed signup, and the log line above each call says which. Never
  // key a wrapper-side failure as `email` — callers read that as BIS's
  // "already signed up" response.
  const fail = (reason) => settle('error', { errors: {}, waitlistError: reason });

  const email = typeof settings.customer_email === 'string' ? settings.customer_email.trim() : '';
  if (!email) {
    log('[Waitlist API] Missing customer_email — refusing to register', settings);
    fail('no_email');
    return false;
  }

  if (typeof window.BIS === 'undefined' || typeof window.BIS.create !== 'function') {
    log('[Waitlist API] BIS not available — app may not be installed or script blocked');
    fail();
    return false;
  }

  log(
    '[Waitlist API] Registering: variant=' + settings.variant_id + ', product=' + settings.product_id
  );

  try {
    const request = window.BIS.create(email, settings.variant_id, settings.product_id, {
      accepts_marketing: true,
      channel: 'email',
    });

    if (!request || typeof request.then !== 'function') {
      log('[Waitlist API] BIS.create returned no thenable', request);
      fail();
      return false;
    }

    request.then((response) => {
      if (response && response.status == 'OK') {
        log('[Waitlist API] Success', response);
        settle('success', response);
      } else {
        log('[Waitlist API] Error response', response);
        settle('error', response || { errors: {} });
      }
      if (typeof callback === 'function') callback(response, settings);
    });

    // Native promise only; BIS.Promise has no catch().
    if (typeof request.catch === 'function') {
      request.catch((err) => {
        log('[Waitlist API] Request failed', err);
        fail();
      });
    }
  } catch (err) {
    log('[Waitlist API] Unexpected error', err);
    fail();
    return false;
  }
}

window.api = { addToWaitlist };

log('[Waitlist API] registered');
